/**
 * Marketing -> Contacts: the address ledger and the shared-address report.
 *
 * ONE PAGE AND TWO TABS, because they are two answers to one question and the
 * plan names them together. The ledger says what we have done with an address;
 * the shared report says which addresses we deliberately do not touch and who
 * is behind them. Both are lists of mailboxes, both open the same panel, and
 * splitting them into two routes would put the exclusion out of sight of the
 * thing it excludes from.
 *
 * ⚠ THE STRIP AT THE TOP HAS A WORD IN IT THAT LOOKS LIKE A SYNONYM AND IS NOT.
 * "In the ledger" counts addresses a campaign ENROLLED, which sends nothing.
 * "Written to" counts the ones a letter actually left for. On the local mirror
 * on 2026-08-22 those were 846 and 2. They are two tiles rather than one for
 * exactly that reason.
 *
 * Read-only, deliberately. Suppressing an address and erasing one both live on
 * the suppression page with the guards that belong to them, and a browsing
 * screen must not grow a second door into either.
 */
import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { AtSign, ChevronLeft, MailCheck, Ban, Inbox, Users2 } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import type { StaffUser } from "../../../../lib/auth";
import { getContactStats, type ContactStats } from "../../../../lib/marketingContacts";
import { Card, PageHead, PillTabs } from "../../../ui";
import { PopHost } from "../../../shell/Pop";
import { Note, num } from "../shared";
import { AddressModal } from "./AddressModal";
import { LedgerTab } from "./LedgerTab";
import { SharedTab } from "./SharedTab";

type Tab = "ledger" | "shared";

export function ContactsPage({ user }: { user: StaffUser }) {
  const t = useT();
  const [tab, setTab] = useState<Tab>("ledger");
  const [open, setOpen] = useState<string | null>(null);
  const [stats, setStats] = useState<ContactStats | null>(null);

  useEffect(() => {
    // Its own request, not folded into the list: the strip describes the whole
    // ledger and the list describes one filtered page of it, so a shared answer
    // would have to be recomputed on every keystroke to stay true.
    getContactStats()
      .then(setStats)
      .catch(() => setStats(null));
  }, []);

  return (
    // One open menu at a time across both tabs' toolbars.
    <PopHost>
      <PageHead
        title={t("marketing.contacts.title")}
        sub={t("marketing.contacts.intro")}
        actions={
          <Link to="/marketing/campaigns" className="crm-btn">
            <ChevronLeft className="size-4" />
            {t("marketing.back")}
          </Link>
        }
      />

      {/* Four columns as a CSS variable, never as an inline grid template: the
          responsive folds override `--crm-quick-cols`, and an inline template
          cannot be outranked by any stylesheet rule. */}
      <Card className="crm-quick" style={{ "--crm-quick-cols": 4 } as CSSProperties}>
        <Quick
          icon={<Inbox className="size-4" />}
          label={t("marketing.contacts.quick.total")}
          value={num(stats?.total ?? null)}
        />
        <Quick
          icon={<MailCheck className="size-4" />}
          label={t("marketing.contacts.quick.contacted")}
          value={num(stats?.contacted ?? null)}
        />
        <Quick
          icon={<Ban className="size-4" />}
          label={t("marketing.contacts.quick.stopped")}
          value={num(stats?.stopped ?? null)}
        />
        <Quick
          icon={<AtSign className="size-4" />}
          label={t("marketing.contacts.quick.freemail")}
          value={num(stats?.freemail ?? null)}
        />
      </Card>

      <Note tone="info" className="mb-3.5">
        {t("marketing.contacts.ledgerNote")}
      </Note>

      <PillTabs
        className="mb-3.5"
        value={tab}
        onChange={setTab}
        tabs={[
          { value: "ledger", label: t("marketing.contacts.tab.ledger"), icon: <Inbox className="size-4" /> },
          { value: "shared", label: t("marketing.contacts.tab.shared"), icon: <Users2 className="size-4" /> },
        ]}
      />

      {tab === "ledger" ? <LedgerTab onOpen={setOpen} /> : <SharedTab onOpen={setOpen} />}

      {open ? <AddressModal address={open} user={user} onClose={() => setOpen(null)} /> : null}
    </PopHost>
  );
}

/**
 * One cell of the quick strip.
 *
 * Local, like every other one in this app: there is no shared component for it
 * (checked 2026-08-22 - eight sections each define their own), and the markup
 * is three spans. Importing a `Quick` from `../../../ui` does not compile.
 */
function Quick({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <span className="crm-q">
      <span className="crm-q-tile">{icon}</span>
      <span>
        <span className="crm-q-lab block">{label}</span>
        <span className="crm-q-val block">{value}</span>
      </span>
    </span>
  );
}
